import { useQueryClient } from "@tanstack/react-query";
import { SystemArtifactState } from "@lib/signalr/signalr.types";
import { notifications } from "@mantine/notifications";
import { GetSystemResponse } from "@features/systems/domain/systems.types";
import { systemKeys } from "@api/systems/systemQueries";
import { Button, Stack, Text } from "@mantine/core";
import { IconCheck, IconDownload } from "@tabler/icons-react";
import { systemService } from "@api/systems/systemService";
import { downloadFile } from "@utils/browser.utils.ts";
import { addNewArtifact } from "@features/systems/utils/artifactTracking";

export function useArtifactNotifications() {
  const queryClient = useQueryClient();

  const showArtifactNotification = (artifactId: string, state: SystemArtifactState, systemId: string, message?: string) => {
    const systemData = queryClient.getQueryData<GetSystemResponse>(systemKeys.detail(systemId));
    const displayName = systemData?.currentEnvironment?.machineName ?? systemId;

    // Use single notification ID per artifact to enable state transitions
    const notificationId = `artifact-${artifactId}`;

    const handleDownload = async () => {
      try {
        const data = await queryClient.fetchQuery({
          queryKey: systemKeys.artifactDownloadUri(artifactId),
          queryFn: () => systemService.getSystemArtifactDownloadUri({artifactId})
        });

        if (!data.downloadUrl) {
          throw new Error('No download URL provided');
        }

        downloadFile(data.downloadUrl);

        notifications.update({
          id: notificationId,
          color: 'green',
          title: `${displayName} Notification`,
          icon: <IconCheck size={18}/>,
          message: 'Artifact downloaded successfully',
          autoClose: 1000,
        });
      } catch (error) {
        console.error('Failed to download artifact:', error);
        notifications.show({
          title: 'Download Failed',
          message: 'Please try again later',
          color: 'red'
        });
      }
    };

    switch (state) {
      case SystemArtifactState.Requested:
        // Initial state - show notification
        notifications.show({
          id: notificationId,
          title: `${displayName} Notification`,
          message: message,
          color: 'blue',
          autoClose: false,
        });
        return;

      case SystemArtifactState.RequestReceived:
        // Update existing notification
        notifications.update({
          id: notificationId,
          title: `${displayName} Notification`,
          message: message,
          color: 'violet',
          autoClose: false,
        });
        return;

      case SystemArtifactState.Uploading:
        // Update existing notification
        notifications.update({
          id: notificationId,
          title: `${displayName} Notification`,
          message: message,
          loading: true,
          color: 'orange',
          autoClose: false,
        });
        return;

      case SystemArtifactState.Uploaded:
        // Track as new artifact in sessionStorage
        addNewArtifact(systemId, artifactId);

        // Update to final success state - stays open until manually dismissed
        notifications.update({
          id: notificationId,
          title: `${displayName} Notification`,
          color: 'green',
          icon: <IconCheck size={18}/>,
          loading: false,
          autoClose: false,
          withCloseButton: true,
          message: (
            <Stack gap="xs">
              <Text>{message}</Text>
              <Button
                size="xs"
                leftSection={<IconDownload size={16}/>}
                onClick={handleDownload}
                w="fit-content"
                aria-label={`download-artifact-${artifactId}`}
              >
                Download
              </Button>
            </Stack>
          ),
        });
        return;

      case SystemArtifactState.Failed:
        // Hide any existing notification and show failed state
        // Using hide+show instead of update to handle cases where no prior notification exists
        // (e.g., user joined page mid-operation or operation was triggered via API)
        notifications.hide(notificationId);
        notifications.show({
          id: notificationId,
          title: `${displayName} Notification`,
          message: message,
          color: 'red',
          autoClose: 8000,
        });
        return;

      default:
        console.warn(`[SignalR] Unknown artifact state for notification: ${state}`);
    }
  };

  return {showArtifactNotification};
}